/**
 * Eager vs lazy namespace contract for @linkhr/i18n.
 *
 * - **Eager namespaces** (this constant) ship with the initial app payload.
 *   They hold copy used on every route — button labels, empty-state strings,
 *   validation messages, boundary errors — so those never block first paint
 *   on a network fetch.
 * - **Per-feature namespaces** (employees, leaves, attendance, payroll, …)
 *   lazy-load via `i18next-http-backend` on the first `t()` call that hits
 *   them from within a feature slice. Apps serve those bundles from
 *   `/locales/{{lng}}/{{ns}}.json`.
 *
 * Requirements: I18N-02 (namespace strategy), I18N-06 (fallback chain).
 */

export const EAGER_NAMESPACES = ["common", "errors", "validation", "coming-soon"] as const;
export type EagerNamespace = (typeof EAGER_NAMESPACES)[number];

/**
 * Lazy per-feature namespaces. Not bundled with the initial payload; loaded
 * on first `t("<ns>:<key>")` hit via `i18next-http-backend` from
 * `/locales/{{lng}}/{{ns}}.json`. Apps must serve these bundles from their
 * `public/locales/` directory (or duplicate via static copy at build).
 *
 * Phase 1 Plan 05 added the `auth` namespace — the login / OTP / reset
 * screens are pre-authenticated but the namespace itself is small (~2KB per
 * locale) and the only place it's referenced is the /login, /auth/forgot,
 * /auth/reset routes, so fetching it on route entry keeps the main bundle
 * lean without flashing raw keys in the UI (the auth routes mount their
 * layout with a Suspense-friendly splash).
 */
export const AUTH_NAMESPACE = "auth" as const;
export type AuthNamespace = typeof AUTH_NAMESPACE;

export const SUPPORTED_LOCALES = ["en", "ar"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
